import React, { useState } from "react";
import {
  UserIcon,
  UserGroupIcon,
  UsersIcon,
  SparklesIcon,
} from "@heroicons/react/24/solid";
import { getFileByModule } from "../../services/FilesService/FilesService";
import toast from "react-hot-toast";
import { useFilePreview } from "../../hooks/useFilePreview";
import FilePreviewModal from "../../components/FilePreviewModal/FilePreviewModal";
import { downloadFile } from "../../utils/fileUtils";

const ValuesPage = () => {
  const [isDownloading, setIsDownloading] = useState(null); // Track which card is downloading

  // Use the reusable file preview hook
  const {
    isPreviewOpen,
    previewUrl,
    isLoading: isPreviewLoading,
    openPreview,
    closePreview,
  } = useFilePreview();

  const [selectedCard, setSelectedCard] = useState(null);

  const cards = [
    {
      title: "Құндылыққа негізделген тәрбие",
      description:
        "Баланың тұлғалық қалыптасуына ықпал ететін негізгі адами құндылықтарды күнделікті өмірмен байланыстырып, балабақшада мақсатты және жүйелі түрде дамыту",
      baseColor: "green-500",
      ringColor: "ring-green-500",
      textColor: "text-green-500",
      bgColor: "bg-green-500",
      icon: <SparklesIcon className="w-10 h-10 text-green-500" />,
      group: "tarbie",
    },
    {
      title: "Баламен жұмыс",
      description:
        "Құндылықты сіңіру – бала тұлғасының негізін қалыптастырып, оның саналы, мейірімді және жауапты азамат болып өсуіне жол ашады",
      baseColor: "amber-500",
      ringColor: "ring-amber-500",
      textColor: "text-amber-500",
      bgColor: "bg-amber-500",
      icon: <UserIcon className="w-10 h-10 text-amber-500" />,
      group: "bala",
    },
    {
      title: "Ата-анамен жұмыс",
      description:
        "Құндылықтар ата-анамен жұмыста бірлескен тәрбиенің негізін қалайды, өзара түсіністік пен үйлесімді қарым-қатынас орнатады",
      baseColor: "[#674DA2]",
      ringColor: "ring-[#674DA2]",
      textColor: "text-[#674DA2]",
      bgColor: "bg-[#674DA2]",
      icon: <UsersIcon className="w-10 h-10 text-[#674DA2]" />,
      group: "ata_ana",
    },
    {
      title: "Ұжыммен жұмыс",
      description:
        "Құндылықтар ұжымда сенім мен ынтымақтастықты арттырып, бірлескен мақсаттарға тиімді жетуге мүмкіндік береді",
      baseColor: "red-500",
      ringColor: "ring-red-500",
      textColor: "text-red-500",
      bgColor: "bg-red-500",
      icon: <UserGroupIcon className="w-10 h-10 text-red-500" />,
      group: "ujym",
    },
  ];

  const fetchFileMeta = async (group) => {
    try {
      const response = await getFileByModule({
        module_code: "qundylyq",
        group,
      });

      if (!response?.file) {
        toast.error("Файл табылмады.");
        return null;
      }
      return response.file;
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Файл табылмады немесе қате болды.");
      return null;
    }
  };

  const handlePreview = async (card) => {
    const file = await fetchFileMeta(card.group);
    if (!file) return;

    setSelectedCard(card);
    await openPreview(file.file_url, file.file_name);
  };

  const handleDownload = async (group, cardIndex) => {
    setIsDownloading(cardIndex);
    try {
      const file = await fetchFileMeta(group);
      if (!file) return;

      downloadFile(file.file_url, file.file_name);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Файл жүктеу кезінде қате болды.");
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="flex flex-col items-center px-4 py-16">
      <h1 className="text-3xl text-[#674DA2] mb-6 text-center">
        Біздің құндылық
      </h1>
      <p className="text-md text-[#674DA2] max-w-3xl text-center mb-16">
        Құндылықтар – бұл адамның және қоғамның негізгі бағдарлары, олар шешім
        қабылдауда, қарым-қатынаста және өмір сүруде бағыт-бағдар береді.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:max-w-4xl max-w-2xl w-full">
        {cards.map((card, index) => (
          <div
            key={index}
            className={`group bg-white ${card.ringColor} ring-2 rounded-2xl shadow-lg p-8 transition-all transform hover:scale-105 hover:${card.bgColor} hover:text-white hover:ring-transparent flex flex-col items-center justify-center text-center min-h-[280px] relative`}
          >
            {/* Default state - shows icon and description */}
            <div
              className={`flex flex-col items-center justify-center transition-opacity duration-300 group-hover:opacity-0 ${card.textColor}`}
            >
              <div className="mb-4">{card.icon}</div>
              <p className="text-base leading-relaxed">{card.description}</p>
            </div>

            {/* Hover state - shows title and action buttons */}
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white px-6`}
            >
              <h2 className="text-xl font-semibold mb-6">{card.title}</h2>

              {/* Action buttons */}
              <div className="flex gap-3 w-full max-w-xs">
                {/* Preview button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(card);
                  }}
                  disabled={isPreviewLoading}
                  className={`flex-1 bg-white font-semibold px-4 py-2 rounded hover:bg-gray-100 transition text-sm ${card.textColor} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                >
                  {isPreviewLoading ? (
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>Көру</span>
                    </>
                  )}
                </button>

                {/* Download button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(card.group, index);
                  }}
                  disabled={isDownloading === index}
                  className={`flex-1 bg-white font-semibold px-4 py-2 rounded hover:bg-gray-100 transition text-sm ${card.textColor} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                >
                  {isDownloading === index ? (
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span>Жүктеу</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={previewUrl}
        onClose={closePreview}
        title={selectedCard ? `${selectedCard.title}` : "Алдын ала қарау"}
      />
    </div>
  );
};

export default ValuesPage;
